import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageProvider';
import { QualizLogo } from './QualizLogo';

export function Register({ onRegister, onBack, registrationEnabled = true }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'Student',
    department: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const { language, setLanguage, availableLanguages } = useLanguage();

  useEffect(() => {
    document.title = t('register.pageTitle');
  }, [t]);

  const handleChange = e => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');

    if (!registrationEnabled) {
      setError(t('register.registerDisabled') || 'Registration is currently disabled.');
      return;
    }

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError(t('register.passwordMismatch'));
      return;
    }

    // Validate password length
    if (formData.password.length < 6) {
      setError(t('register.passwordTooShort'));
      return;
    }

    setLoading(true);

    try {
      const registrationData = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: formData.role,
      };

      // Add role-specific fields
      if (formData.role === 'Professor') {
        registrationData.department = formData.department;
      }

      const response = await api.register(registrationData);

      if (response.success) {
        const userData = {
          ...response.user,
          token: response.token,
        };
        console.log('User registered:', userData);
        onRegister(userData);
      } else {
        setError(response.message || t('register.registerFailed'));
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.message || t('register.registerError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="register-div-main"
      className="min-h-screen bg-gradient-to-br from-blue-500 to-white-600 flex items-center justify-center p-4"
    >
      {!registrationEnabled ? (
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-2xl">
          <div className="text-center space-y-3">
            <h1 className="text-2xl font-semibold text-gray-900">Registration Disabled</h1>
            <p className="text-gray-600">
              New account registration is currently turned off by the system administrator.
            </p>
            <button
              className="mt-4 px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
              onClick={onBack}
            >
              {t('auth.signIn')}
            </button>
          </div>
        </div>
      ) : (
        <div
          data-testid="register-div-container"
          className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        >
          <div
            data-testid="register-div-header"
            className="text-center mb-6 flex flex-col items-center gap-3"
          >
            <QualizLogo size={64} />
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">Qualiz</h2>
              <p className="text-sm text-gray-500">Educational Quality Analysis</p>
            </div>
            <div>
              <h1 data-testid="register-h1-title" className="text-3xl font-bold text-gray-900 mb-2">
                {t('register.createAccount')}
              </h1>
              <p data-testid="register-p-subtitle" className="text-gray-600">
                {t('register.subtitle')}
              </p>
            </div>
          </div>

          {error && (
            <div
              data-testid="register-div-error"
              className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm"
            >
              {error}
            </div>
          )}

          <form data-testid="register-form" onSubmit={handleSubmit} className="space-y-4">
            <div data-testid="register-div-name">
              <label
                data-testid="register-label-name"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t('register.fullName')}
              </label>
              <input
                data-testid="register-input-name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder={t('register.fullNamePlaceholder')}
                required
                disabled={loading}
              />
            </div>

            <div data-testid="register-div-email">
              <label
                data-testid="register-label-email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t('register.email')}
              </label>
              <input
                data-testid="register-input-email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder={t('register.emailPlaceholder')}
                required
                disabled={loading}
              />
            </div>

            <div data-testid="register-div-password">
              <label
                data-testid="register-label-password"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t('register.password')}
              </label>
              <input
                data-testid="register-input-password"
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder={t('register.passwordPlaceholder')}
                required
                disabled={loading}
              />
            </div>

            <div data-testid="register-div-confirm-password">
              <label
                data-testid="register-label-confirm-password"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t('register.confirmPassword')}
              </label>
              <input
                data-testid="register-input-confirm-password"
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder={t('register.confirmPasswordPlaceholder')}
                required
                disabled={loading}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
              <div data-testid="register-div-role">
                <label
                  data-testid="register-label-role"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  {t('register.role')}
                </label>
                <select
                  data-testid="register-select-role"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  required
                  disabled={loading}
                >
                  <option data-testid="register-option-student" value="Student">
                    {t('register.roleStudent')}
                  </option>
                  <option data-testid="register-option-professor" value="Professor">
                    {t('register.roleProfessor')}
                  </option>
                </select>
              </div>

              <div data-testid="register-language-select">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('language.label')}
                </label>
                <div className="flex items-center gap-3">
                  <select
                    data-testid="register-select-language"
                    value={language}
                    onChange={e => setLanguage(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    {availableLanguages.map(lang => (
                      <option key={lang.code} value={lang.code}>
                        {lang.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {formData.role === 'Professor' && (
              <div data-testid="register-div-department">
                <label
                  data-testid="register-label-department"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  {t('register.department')}
                </label>
                <input
                  data-testid="register-input-department"
                  type="text"
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder={t('register.departmentPlaceholder')}
                  disabled={loading}
                />
              </div>
            )}

            <button
              data-testid="register-button-submit"
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-500 transition font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {loading ? t('register.creating') : t('register.submit')}
            </button>
          </form>

          <div data-testid="register-div-back-link" className="mt-4 text-center">
            <button
              data-testid="register-button-back"
              onClick={onBack}
              className="text-blue-600 hover:text-blue-500 text-sm font-medium"
              disabled={loading}
            >
              {t('register.backToLogin')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
